import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import BillingItemsRepository from '@/repositories/BillingItemsRepository';
import { BillingItem } from '@/types/encounter';
import RepositoryResponse from '@/types/repositoryResponse';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

interface UseBillingItemRefreshProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useBillingItemRefresh = ({ onSuccess, onError }: UseBillingItemRefreshProps = {}) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const billingItemsRepository = BillingItemsRepository();

  return useMutation({
    mutationFn: ({
      billingItemId
    }: {
      billingItemId: string;
    }): Promise<RepositoryResponse<BillingItem>> => {
      return billingItemsRepository.refreshItem({ billingItemId });
    },
    onSuccess: (response) => {
      if (response.isError) {
        addMessage({ level: 'error', text: 'Failed to refresh billing item' });
        onError && onError(new Error('Failed to refresh billing item'));
      } else {
        addMessage({ level: 'success', text: 'Billing item refreshed successfully' });
        // Invalidate relevant queries that might need to be refetched
        queryClient.invalidateQueries({ queryKey: ['encounter'] });
        onSuccess && onSuccess();
      }
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        addMessage(
          mapAxiosErrorMessages(
            error instanceof AxiosError ? error : ({ message: error.message } as AxiosError)
          )
        );
        onError && onError(error);
      }
    }
  });
};

export default useBillingItemRefresh;
